import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { AdditionalFieldTypes } from 'enums/onboarding/crm';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { formatFromJSON, formatToJSON } from 'helpers/crmHelpers';
import { AdditionalFieldFormItemModel } from 'components/Forms/FormComponents/AdditionalField';
import { additionalQuestionsStepValidationSchema } from 'validations/intake-form/intake-form-steps';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { Form, SectionIntro } from '@ui';

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

export interface AdditionalQuestionFormItem
  extends AdditionalFieldFormItemModel {
  isNotApplicableOrAvailable: boolean;
  notAvailableValue: string;
}

export interface FormValuesModel extends SubmitButtonsFormValues {
  additionalQuestions: AdditionalQuestionFormItem[];
}

const AdditionalQuestions = ({
  disabled,
  renderNavigateButtonInTitle,
}: IProps) => {
  const { t } = useTranslation('onboarding');
  const {
    intakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }

    const formattedFields: AdditionalQuestionFormItem[] =
      intakeForm.additionalQuestions.map((additionalQuestion) => {
        const field = additionalQuestion.field;

        let formattedValue: any = '';

        if (additionalQuestion.answer?.valueJSON) {
          formattedValue = formatFromJSON(
            field.type,
            additionalQuestion.answer.valueJSON,
          );

          if (
            [AdditionalFieldTypes.Form, AdditionalFieldTypes.Document].includes(
              field.type,
            ) &&
            formattedValue
          ) {
            if (formattedValue && Array.isArray(formattedValue)) {
              formattedValue = formattedValue
                .map((e) => {
                  const document =
                    intakeForm && intakeForm.metadata.documents[e];
                  if (document) {
                    return {
                      id: document.id,
                      name: document.files[0].name,
                      fileId: document.files[0].id,
                      file: document.files[0],
                    };
                  }
                })
                .filter((e) => !!e);
            }
          }
        }

        return {
          id: additionalQuestion._id,
          name: field.name,
          description: field.description,
          type: field.type,
          isNotApplicableOrAvailable:
            typeof additionalQuestion.answer?.isProvided == 'boolean'
              ? !additionalQuestion.answer.isProvided
              : false,
          notAvailableValue: additionalQuestion.answer?.reason || '',
          value: formattedValue,
          relatedTo: 'contact',
          options: {
            onboardingRequired: field.options.onboardingRequired,
            isForRegulatedOnly: false,
            documentRequiresCertification:
              field.options.documentRequiresCertification,
            selectOptions: field.options.selectOptions.length
              ? field.options.selectOptions
              : [],
            formDocument: field.options.formDocument,
          },
        };
      });

    return {
      submitType: null,
      additionalQuestions: formattedFields,
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }

    const additionalQuestionAnswers = values.additionalQuestions.map((e) => ({
      questionId: e.id,
      answer: e.isNotApplicableOrAvailable
        ? {
            isProvided: false,
            reason: e.notAvailableValue,
          }
        : e.value
          ? {
              isProvided: true,
              valueJSON: formatToJSON(e.type, e.value),
            }
          : null,
    }));

    await updateIntakeFormFields(undefined, additionalQuestionAnswers);
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleVariant={disabled ? 'h5' : undefined}
        titleColor={colorsTheme.colorWhite}
        title={
          <DivAlignCenter>
            {t('intake_form.additional_questions.title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="additional_questions" />
            ) : null}
          </DivAlignCenter>
        }
        description={t('intake_form.additional_questions.description')}
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={additionalQuestionsStepValidationSchema}
      />
    </>
  );
};

export default AdditionalQuestions;
